import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/system";
import {
  Autocomplete,
  Button,
  Dialog,
  TextField,
  Typography,
  MenuItem,
  Snackbar,
  Alert,
  Skeleton,
  IconButton,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import {
  ControlPoint,
  Close,
  Search,
  SwapVert,
  ArrowUpward,
  ArrowDownward,
  CreateNewFolderOutlined,
  FolderOpenOutlined,
  WarningAmberRounded,
} from "@mui/icons-material";
import DashboardHeaderSection from "../common/dashboardheader/DashboardHeader";
import ReportsTableComponent from "./common/ReportsTableComponent";

import { useFetchReports } from "../../react-query/hooks/useFetchReport";
import { useFetchReportGroups, useCreateReportGroup, useDeleteReportGroup } from "../../react-query/hooks/useReportGroups";
import reportServices from "../../react-query/services/reportServices";
import paymentportalservice from "../../services/paymentportalservice";
import CreateReportDialog from "./components/CreateReportDialog";
import { useMediaQuery, useTheme } from "@mui/material";
import NoReport from "../../assets/images/no_reports.png";
import GetCookieByName from "../../hooks/GetCookie";

export const Reports = () => {
  const [open, setOpen] = useState(false);
  const [openAlert, setOpenAlert] = useState(false);
  const [reports, setReports] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // ── Report Groups (parent reports) ──────────────────────────────────────────
  const { data: groupsData } = useFetchReportGroups();
  const groups = useMemo(() => groupsData?.data ?? [], [groupsData]);
  const createGroupMutation = useCreateReportGroup();
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupParams, setGroupParams] = useState({ name: "", brand: null });
  const [groupError, setGroupError] = useState(false);
  // Brand list for the group dialog's Autocomplete below — CreateReportDialog
  // fetches its own copy for the report dialog now that it's a standalone
  // component, so this page only needs it for the group-creation form.
  const [brandsList, setBrandsList] = useState([]);
  useEffect(() => {
    paymentportalservice.FetchBrandsData().catch(() => []).then((brandsRaw) => {
      const normalized = (Array.isArray(brandsRaw) ? brandsRaw : (brandsRaw?.data ?? []))
        .map((b) => ({ _id: String(b._id || b.id || b.brandId || ""), name: b.name || b.brandName || b.title || "" }))
        .filter((b) => b._id && b.name)
        .sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
      setBrandsList(normalized);
    });
  }, []);

  const handleOpenGroupDialog = () => setGroupDialogOpen(true);
  const handleCloseGroupDialog = () => {
    setGroupDialogOpen(false);
    setGroupParams({ name: "", brand: null });
    setGroupError(false);
  };
  const handleCreateGroup = async () => {
    if (!groupParams.name.trim()) {
      setGroupError("Please enter a group name.");
      return;
    }
    try {
      const payload = {
        name: groupParams.name.trim(),
        ...(groupParams.brand ? { brand: { _id: groupParams.brand._id, name: groupParams.brand.name } } : {}),
      };
      await createGroupMutation.mutateAsync(payload);
      handleCloseGroupDialog();
      setSuccessMessage("Report group created successfully!");
      setOpenAlert(true);
    } catch (err) {
      setGroupError(err?.response?.data?.message || "Failed to create report group. Please try again.");
    }
  };

  // ── Delete Report Group ─────────────────────────────────────────────────────
  // Mirrors ReportsTableComponent.jsx's individual-report delete flow: a
  // confirm dialog for the creator, a separate "not allowed" dialog for anyone
  // else in the org who can view the group but shouldn't be able to delete it.
  const deleteGroupMutation = useDeleteReportGroup();
  const [pendingDeleteGroup, setPendingDeleteGroup] = useState(null);
  const [notAllowedGroup, setNotAllowedGroup] = useState(null);
  const [deleteGroupError, setDeleteGroupError] = useState(null);

  const handleDeleteGroupClick = (group) => {
    if (!group.isMine) {
      setNotAllowedGroup(group);
    } else {
      setPendingDeleteGroup(group);
    }
  };

  const handleCancelDeleteGroup = () => {
    if (deleteGroupMutation.isPending) return;
    setPendingDeleteGroup(null);
    setDeleteGroupError(null);
  };

  const handleConfirmDeleteGroup = async () => {
    if (!pendingDeleteGroup) return;
    setDeleteGroupError(null);
    try {
      await deleteGroupMutation.mutateAsync({ groupId: pendingDeleteGroup.id });
      setPendingDeleteGroup(null);
      setSuccessMessage("Report group deleted. Its reports were kept and are ungrouped.");
      setOpenAlert(true);
    } catch (err) {
      setDeleteGroupError(err?.response?.data?.message || "Failed to delete report group. Please try again.");
    }
  };

  // Decode current user ID from auth JWT (used for creator-only delete guard)
  const currentUserId = useMemo(() => {
    try {
      const token = JSON.parse(GetCookieByName("auth"))?.token;
      if (!token) return null;
      return JSON.parse(atob(token.split('.')[1]))._id;
    } catch { return null; }
  }, []);

  // Filter + sort state
  const [nameSearch, setNameSearch]           = useState('');
  const [createdByFilter, setCreatedByFilter] = useState('');
  const [sortBy,  setSortBy]  = useState('status'); // 'date' | 'name' | 'posts' | 'status'
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'

  const SORT_OPTIONS = [
    { value: 'date',   label: 'Date Created' },
    { value: 'name',   label: 'Report Name'  },
    { value: 'posts',  label: 'Posts Live'   },
    { value: 'status', label: 'Status'       },
  ];

  const uniqueCreators = useMemo(() => {
    if (!reports) return [];
    const seen = new Map();
    reports.forEach(r => {
      if (r.createdby?._id && !seen.has(r.createdby._id)) seen.set(r.createdby._id, r.createdby);
    });
    return [...seen.values()];
  }, [reports]);

  // Report groups shown as rows in the same table as individual reports —
  // identified by a small folder icon (see ReportsTableComponent) rather than
  // a separate section. Normalized to the same `_id`/`name` shape a report row
  // has so the filter/sort logic below needs no group-specific branching.
  const groupRows = useMemo(() => groups.map((g) => ({
    _id: g.id,
    rowType: "group",
    name: g.name,
    brand: g.brand,
    platforms: g.platforms,
    reportCount: g.reportCount,
    totalPosts: g.totalPosts,
    activeCount: g.activeCount,
    stoppedCount: g.stoppedCount,
    isMine: g.isMine,
    reports: g.reports,
  })), [groups]);

  const allRows = useMemo(() => [...groupRows, ...(reports || [])], [groupRows, reports]);

  const filteredRows = useMemo(() => {
    const filtered = allRows.filter(r => {
      if (nameSearch && !(r.name || '').toLowerCase().includes(nameSearch.toLowerCase())) return false;
      // Group rows have no creator info of their own — a specific creator
      // filter naturally excludes them rather than needing a special case.
      if (createdByFilter && String(r.createdby?._id) !== createdByFilter) return false;
      return true;
    });

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return dir * (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' });
        case 'posts':
          return dir * ((a.postCount ?? 0) - (b.postCount ?? 0));
        case 'status': {
          // asc = Active first (0), desc = Stopped first (1)
          const sa = a.autoRefreshStopped ? 1 : 0;
          const sb = b.autoRefreshStopped ? 1 : 0;
          return dir * (sa - sb);
        }
        case 'date':
        default: {
          // MongoDB ObjectId first 4 bytes = unix timestamp
          const ta = parseInt((a._id?.toString() || '').slice(0, 8), 16) || 0;
          const tb = parseInt((b._id?.toString() || '').slice(0, 8), 16) || 0;
          return dir * (ta - tb);
        }
      }
    });
  }, [allRows, nameSearch, createdByFilter, sortBy, sortDir]);

  const handleAlertClose = () => setOpenAlert(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // ungrouped:true — grouped reports are shown nested under their parent group
  // in the "Parent Reports" section above, not duplicated in this flat list.
  const { isError, data, isLoading } = useFetchReports({ ungrouped: true });

  useEffect(() => {
    setReports(data?.data);
  }, [data]);

  const handleDeleteReport = async (id) => {
    const response = await reportServices.DeleteReport(id);
    if (response.status !== 200) throw new Error(`Delete failed: ${response.status}`);
    setReports((prev) => prev.filter((r) => r._id !== id));
    setSuccessMessage("Report deleted successfully.");
    setOpenAlert(true);
  };

  return (
    <>
      <Snackbar
        open={openAlert}
        autoHideDuration={8000}
        onClose={handleAlertClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert onClose={handleAlertClose} severity="success" sx={{ width: "100%" }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <DashboardHeaderSection hideSearch />
      <Box
        width={{ xs: "100%", lg: `calc(100% - 80px)` }}
        marginLeft={{ xs: "0px", lg: `80px` }}
        marginTop={8}
        padding={1}
        sx={{ height: "100vh", bgcolor: "#F0F2F5", overflow: "hidden", display: "flex", flexDirection: "column" }}
      >
        {/* ── Page header ──────────────────────────────────────────────────── */}
        <Box sx={{ display: "flex", justifyContent: "space-between", flexDirection:{
          xs:'column',
          sm:'row'
        },alignItems:{xs:'stretch',sm:'center'},px: 0.5 }}>
          <Box>
            <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
              Reports
            </Typography>
            {reports && (
              <Typography sx={{ fontSize: 13, color: "#9CA3AF", mt: 0.3 }}>
                {filteredRows.length === allRows.length
                  ? `${allRows.length} report${allRows.length !== 1 ? "s" : ""} total`
                  : `${filteredRows.length} of ${allRows.length} reports`}
                {groups.length > 0 && ` · ${groups.length} group${groups.length !== 1 ? "s" : ""}`}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
            <Button
              variant="outlined"
              startIcon={<CreateNewFolderOutlined sx={{ fontSize: 17 }} />}
              sx={{
                fontSize: "13.5px",
                textTransform: "none",
                fontWeight: 600,
                color: "#FF5200",
                borderColor: "rgba(255,82,0,0.4)",
                borderRadius: "24px",
                px:{xs:2,sm:3},
                py: 1,
                "&:hover": { borderColor: "#FF5200", background: "rgba(255,82,0,0.06)" },
              }}
              onClick={handleOpenGroupDialog}
            >
              New Group
            </Button>
            <Button
              variant="contained"
              startIcon={<ControlPoint />}
              sx={{
                fontSize: "13.5px",
                textTransform: "none",
                fontWeight: 600,
                background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
                borderRadius: "24px",
                px:{xs:2,sm:3.5},
                py: 1,
                boxShadow: "0 4px 14px rgba(255,82,0,0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
                  opacity: 0.9,
                  boxShadow: "0 6px 20px rgba(255,82,0,0.4)",
                },
              }}
              onClick={handleOpen}
            >
              New Report
            </Button>
          </Box>
        </Box>

        {/* ── Create Report Group Dialog ──────────────────────────────────────── */}
        <Dialog
          open={groupDialogOpen}
          onClose={handleCloseGroupDialog}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: "20px",
              overflow: "hidden",
              border: "1px solid #E5E7EB",
              boxShadow: "0 20px 60px rgba(0,0,0,0.12), 0 4px 16px rgba(0,0,0,0.06)",
            },
          }}
        >
          <Box sx={{ height: 4, background: "linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)", flexShrink: 0 }} />

          <Box sx={{ px: { xs: 2, sm: 3.5 }, pt: 3, pb: 2, display: "flex", alignItems: "flex-start", justifyContent: "space-between", background: "#fff", borderBottom: "1px solid #F3F4F6" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "11px", background: "linear-gradient(135deg, rgba(255,82,0,0.1), rgba(255,140,0,0.05))", border: "1px solid rgba(255,82,0,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <FolderOpenOutlined sx={{ color: "#FF5200", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ color: "#111827", fontWeight: 700, fontSize: 18, lineHeight: 1.2 }}>
                  Create Report Group
                </Typography>
                <Typography sx={{ color: "#9CA3AF", fontSize: 12, mt: 0.3 }}>
                  Group reports across platforms
                </Typography>
              </Box>
            </Box>
            <IconButton size="small" onClick={handleCloseGroupDialog} sx={{ mt: -0.5, mr: -0.5, color: "#9CA3AF", background: "#F9FAFB", border: "1px solid #E5E7EB", "&:hover": { background: "#F3F4F6", color: "#374151" } }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ px: { xs: 2, sm: 3.5 }, py: { xs: 2, sm: 3 }, background: "#fff" }}>
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ color: "#6B7280", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase", mb: 1 }}>
                Group Name
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                placeholder="e.g. Lava — Q3 Campaign"
                size={isMobile ? "small" : "medium"}
                value={groupParams.name}
                onChange={(e) => setGroupParams((prev) => ({ ...prev, name: e.target.value }))}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    background: "#F9FAFB", borderRadius: "12px", color: "#111827", fontSize: 14,
                    "& fieldset": { borderColor: "#E5E7EB" },
                    "&:hover fieldset": { borderColor: "#D1D5DB" },
                    "&.Mui-focused fieldset": { borderColor: "#FF5200", borderWidth: "1.5px" },
                  },
                  "& input::placeholder": { color: "#9CA3AF", opacity: 1 },
                }}
              />
            </Box>

            <Box sx={{ mb: 3.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                <Typography sx={{ color: "#6B7280", fontSize: 11, fontWeight: 700, letterSpacing: "0.07em", textTransform: "uppercase" }}>
                  Brand
                </Typography>
                <Typography sx={{ fontSize: 10.5, color: "#9CA3AF", fontWeight: 500, bgcolor: "#F3F4F6", px: 1, py: 0.25, borderRadius: "6px" }}>
                  optional
                </Typography>
              </Box>
              <Autocomplete
                options={brandsList}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(opt, val) => opt._id === val._id}
                value={groupParams.brand}
                onChange={(_, val) => setGroupParams((prev) => ({ ...prev, brand: val }))}
                clearOnEscape
                renderInput={(inputParams) => (
                  <TextField
                    size={isMobile ? "small" : "medium"}
                    {...inputParams}
                    placeholder={brandsList.length ? "Search brand…" : "No brands available"}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        background: "#F9FAFB", borderRadius: "12px", color: "#111827", fontSize: 14,
                        "& fieldset": { borderColor: "#E5E7EB" },
                        "&:hover fieldset": { borderColor: "#D1D5DB" },
                        "&.Mui-focused fieldset": { borderColor: "#FF5200", borderWidth: "1.5px" },
                      },
                      "& input::placeholder": { color: "#9CA3AF", opacity: 1 },
                      "& .MuiSvgIcon-root": { color: "#9CA3AF" },
                    }}
                  />
                )}
              />
            </Box>

            <Button
              fullWidth
              variant="contained"
              disabled={createGroupMutation.isPending}
              onClick={handleCreateGroup}
              sx={{
                background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
                borderRadius: "12px", py: 1.4, fontSize: 14.5, fontWeight: 600, textTransform: "none",
                boxShadow: "0 4px 16px rgba(255,82,0,0.3)",
                "&:hover": { background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)", opacity: 0.9, boxShadow: "0 8px 24px rgba(255,82,0,0.4)" },
              }}
            >
              {createGroupMutation.isPending ? "Creating…" : "Create Group"}
            </Button>

            {groupError && (
              <Typography sx={{ textAlign: "center", color: "#EF4444", mt: 1.5, fontSize: 12.5 }}>
                {groupError}
              </Typography>
            )}
          </Box>
        </Dialog>

        {/* ── Delete Report Group Confirmation Dialog ─────────────────────────── */}
        <Dialog
          open={Boolean(pendingDeleteGroup)}
          onClose={handleCancelDeleteGroup}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: "20px",
              overflow: "hidden",
              border: "1px solid #E5E7EB",
              boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
            },
          }}
        >
          <Box sx={{ height: 4, background: "linear-gradient(90deg, #EF4444 0%, #F87171 100%)", flexShrink: 0 }} />

          <Box sx={{ px: { xs: 2, sm: 3.5 }, pt: 3, pb: { xs: 2, sm: 3.5 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 40, height: 40, borderRadius: "11px",
                  background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.2)",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
                }}
              >
                <WarningAmberRounded sx={{ color: "#EF4444", fontSize: 20 }} />
              </Box>
              <Box>
                <Typography sx={{ color: "#111827", fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
                  Delete Report Group?
                </Typography>
                <Typography sx={{ color: "#9CA3AF", fontSize: { xs: 10, sm: 12 }, mt: 0.3 }}>
                  This action cannot be undone
                </Typography>
              </Box>
            </Box>

            <Box sx={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: "10px", px: 2, py: 1.4, mb: 2.5 }}>
              <Typography sx={{ color: "#6B7280", fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.4 }}>
                Report Group
              </Typography>
              <Typography sx={{ color: "#111827", fontWeight: 500, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {pendingDeleteGroup?.name}
              </Typography>
            </Box>

            <Typography sx={{ color: "#6B7280", fontSize: 13, lineHeight: 1.6, mb: deleteGroupError ? 1.5 : 3 }}>
              The group will be removed. Its {pendingDeleteGroup?.reportCount ?? 0} report{(pendingDeleteGroup?.reportCount ?? 0) !== 1 ? "s" : ""} will NOT be deleted — they'll just become ungrouped again in the flat list.
            </Typography>

            {deleteGroupError && (
              <Box sx={{ background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.2)", borderRadius: "8px", px: 2, py: 1, mb: 2 }}>
                <Typography sx={{ color: "#EF4444", fontSize: 12.5 }}>{deleteGroupError}</Typography>
              </Box>
            )}

            <Box sx={{ display: "flex", gap: 1.5 }}>
              <Button
                fullWidth
                disabled={deleteGroupMutation.isPending}
                onClick={handleCancelDeleteGroup}
                sx={{
                  color: "#6B7280", background: "#fff", borderRadius: "10px", py: 1.1,
                  fontSize: 13.5, fontWeight: 500, textTransform: "none",
                  border: "1px solid #E5E7EB",
                  "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
                }}
              >
                Cancel
              </Button>
              <Button
                fullWidth
                variant="contained"
                disabled={deleteGroupMutation.isPending}
                onClick={handleConfirmDeleteGroup}
                sx={{
                  background: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)",
                  borderRadius: "10px", py: 1.1, fontSize: 13.5, fontWeight: 600, textTransform: "none",
                  boxShadow: "0 4px 14px rgba(239,68,68,0.3)",
                  "&:hover": { background: "linear-gradient(135deg, #EF4444 0%, #F87171 100%)", opacity: 0.9, boxShadow: "0 6px 20px rgba(239,68,68,0.4)" },
                  "&.Mui-disabled": { background: "#F3F4F6", color: "#9CA3AF", boxShadow: "none" },
                }}
              >
                {deleteGroupMutation.isPending ? "Deleting…" : "Yes, Delete"}
              </Button>
            </Box>
          </Box>
        </Dialog>

        {/* ── Not Allowed Dialog ───────────────────────────────────────────────── */}
        <Dialog
          open={Boolean(notAllowedGroup)}
          onClose={() => setNotAllowedGroup(null)}
          maxWidth="xs"
          fullWidth
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: "20px",
              overflow: "hidden",
              border: "1px solid #E5E7EB",
              boxShadow: "0 20px 60px rgba(0,0,0,0.12)",
            },
          }}
        >
          <Box sx={{ height: 4, background: "linear-gradient(90deg, #FF5200 0%, #FF8C00 100%)", flexShrink: 0 }} />
          <Box sx={{ px: { xs: 2, sm: 3.5 }, pt: 3, pb: { xs: 2, sm: 3.5 } }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box sx={{ width: 40, height: 40, borderRadius: "11px", background: "rgba(255,82,0,0.08)", border: "1px solid rgba(255,82,0,0.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <WarningAmberRounded sx={{ color: "#FF5200", fontSize: 20 }} />
              </Box>
              <Typography sx={{ color: "#111827", fontWeight: 700, fontSize: 17, lineHeight: 1.2 }}>
                Can't Delete This Group
              </Typography>
            </Box>
            <Typography sx={{ color: "#6B7280", fontSize: 13, lineHeight: 1.6, mb: 2.5 }}>
              Only the person who created "{notAllowedGroup?.name}" can delete it.
            </Typography>
            <Button
              fullWidth
              variant="contained"
              onClick={() => setNotAllowedGroup(null)}
              sx={{
                background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
                borderRadius: "10px", py: 1.1, fontSize: 13.5, fontWeight: 600, textTransform: "none",
                boxShadow: "0 4px 14px rgba(255,82,0,0.3)",
                "&:hover": { background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)", opacity: 0.9 },
              }}
            >
              Got it
            </Button>
          </Box>
        </Dialog>

        <CreateReportDialog
          open={open}
          onClose={handleClose}
          onCreated={(newReport) => setReports((prev) => [newReport, ...(prev || [])])}
          onSuccess={(message) => { setSuccessMessage(message); setOpenAlert(true); }}
        />

        {/* ── Filter bar ────────────────────────────────────────────────────── */}
        {allRows.length > 0 && (
          <Box
            sx={{
              mt: 3,
              mb: 0,
              px: { xs: 2, sm: 3.5 },
              py: 2,
              bgcolor: '#fff',
              borderRadius: '14px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              overflowX: "auto",
              whiteSpace: "nowrap",
              "&::-webkit-scrollbar": {
                height: 6,
              },
              "&::-webkit-scrollbar-thumb": {
                background: "#D1D5DB",
                borderRadius: 10,
              },
            }}
          >
            <TextField
              size={isMobile ? "small" : "medium"}
              placeholder="Search by report name…"

              value={nameSearch}
              onChange={e => setNameSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search sx={{ color: '#9CA3AF', fontSize: 17 }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                minWidth:250,
                flex:1,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px', bgcolor: '#F9FAFB', fontSize: 13.5,
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: '#FF5200', borderWidth: '1.5px' },
                },
              }}
            />

            <TextField
              select
              size={isMobile ? "small" : "medium"}
              value={createdByFilter}
              onChange={e => setCreatedByFilter(e.target.value)}
              SelectProps={{
                displayEmpty: true,
                renderValue: val => val
                  ? (uniqueCreators.find(c => String(c._id) === val)?.fullName ?? 'Creator')
                  : 'All Creators',
              }}
              sx={{
                width:180,
                minWidth:180,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px', bgcolor: '#F9FAFB', fontSize: 13.5,
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: '#FF5200', borderWidth: '1.5px' },
                },
                '& .MuiSvgIcon-root': { color: '#9CA3AF' },
              }}
            >
              <MenuItem value="" sx={{ fontSize: 13.5 }}>All Creators</MenuItem>
              {uniqueCreators.map(c => (
                <MenuItem key={c._id} value={String(c._id)} sx={{ fontSize: 13.5 }}>{c.fullName}</MenuItem>
              ))}
            </TextField>

            {/* Sort field */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink:0 }}>
              <SwapVert sx={{ fontSize: 16, color: '#9CA3AF', flexShrink: 0 }} />
              <TextField
                select
                size="small"
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                SelectProps={{
                  MenuProps: {
                    PaperProps: {
                      elevation: 0,
                      sx: {
                        borderRadius: '12px', border: '1px solid #E5E7EB',
                        boxShadow: '0 8px 24px rgba(0,0,0,0.1)', mt: 0.5,
                        '& .MuiMenuItem-root': {
                          fontSize: 13.5, color: '#374151', py: 1,
                          '&:hover': { background: 'rgba(255,82,0,0.06)' },
                          '&.Mui-selected': { background: 'rgba(255,82,0,0.08)', color: '#FF5200', fontWeight: 600 },
                        },
                      },
                    },
                  },
                }}
                sx={{
                  width: 160,
                  minWidth: 160,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px', bgcolor: '#F9FAFB', fontSize: 13.5,
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': { borderColor: '#FF5200', borderWidth: '1.5px' },
                  },
                  '& .MuiSvgIcon-root': { color: '#9CA3AF'},
                }}
              >
                {SORT_OPTIONS.map(o => (
                  <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                ))}
              </TextField>

              {/* Direction toggle */}
              <Tooltip
                title={sortDir === 'desc' ? 'Descending — click for ascending' : 'Ascending — click for descending'}
                arrow placement="top"
              >
                <IconButton
                  size="small"
                  onClick={() => setSortDir(d => d === 'desc' ? 'asc' : 'desc')}
                  sx={{
                    p: '6px', borderRadius: '10px',
                    border: '1px solid #E5E7EB', bgcolor: '#F9FAFB',
                    color: '#6B7280', transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: 'rgba(255,82,0,0.35)',
                      bgcolor: 'rgba(255,82,0,0.06)',
                      color: '#FF5200',
                    },
                  }}
                >
                  {sortDir === 'desc'
                    ? <ArrowDownward sx={{ fontSize: 15 }} />
                    : <ArrowUpward   sx={{ fontSize: 15 }} />}
                </IconButton>
              </Tooltip>
            </Box>

            {(nameSearch || createdByFilter) && (
              <Button
                size="small"
                onClick={() => { setNameSearch(''); setCreatedByFilter(''); }}
                sx={{
                  color: '#9CA3AF', textTransform: 'none', borderRadius: '8px',
                  fontSize: 13,px:{xs:2,sm:3.5},flexShrink:0,
                  '&:hover': { color: '#EF4444', bgcolor: 'rgba(239,68,68,0.06)' },
                }}
              >
                Clear
              </Button>
            )}
          </Box>
        )}

        {/* flex:1 + minHeight:0 lets this region alone scroll within the fixed
            100vh shell above, so the table's TablePagination footer (page
            number + prev/next arrows) stays reachable instead of being
            clipped below an unscrollable viewport — the header/search/filter
            rows above stay pinned since they're plain flex-column siblings. */}
        <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
          {isLoading ? (
            <>
              <br /><br /><br /><br />
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
              <Skeleton animation="wave" />
            </>
          ) : allRows.length > 0 ? (
            <ReportsTableComponent
              loading={isLoading}
              rows={filteredRows}
              onDelete={handleDeleteReport}
              onDeleteGroup={handleDeleteGroupClick}
              currentUserId={currentUserId}
            />
          ) : (
            <Box sx={{ textAlign: "center" }}>
              <img src={NoReport} height={300} alt="No reports found" />
              <Typography color={"red"} variant="h6">
                No Report Found
              </Typography>
              <Button
                variant="contained"
                sx={{
                  fontSize: "12px",
                  marginRight: 2,
                  textTransform: "capitalize",
                  background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
                  borderRadius: "24px",
                  boxShadow: "0 4px 14px rgba(255,82,0,0.3)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #FF5200 0%, #FF8C00 100%)",
                    opacity: 0.9,
                  },
                }}
                onClick={handleOpen}
              >
                <ControlPoint sx={{ fontSize: "16px" }} />
                &nbsp;Create Report
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Reports;
